/**
 * Sync groups output view — ports SyncGroups.jsx from the design handoff.
 * PanelHead with metrics (Groups, Avg adherence, Drifting), one card per
 * temperature sync group showing required windows (outlined) overlaid with
 * actual production runs (solid) per member line + adherence %.
 *
 * Previously named "Temperature" view; the tab label in ScheduleFullGantt
 * was updated to "Sync groups" to match the design.
 */
import { memo, useMemo } from 'react'
import { Box, Tooltip } from '@mui/material'
import SyncIcon from '@mui/icons-material/Sync'
import ThermostatIcon from '@mui/icons-material/Thermostat'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import { TOKENS, TYPE, FONT_MONO, FONT_SANS, SchChip } from './SchedulerDesign'
import { InputRow } from 'Features/SchedulerView/SchedulerView.types'
import {
  SynchronizedLinesRowType,
  ProductionRequirementRowType,
} from './Scheduler.types'

// ─── Constants ────────────────────────────────────────────────────────────────

const TOTAL_HOURS = 168
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const pct = (h: number) => `${(h / TOTAL_HOURS) * 100}%`
const SYNC_COLORS = [
  TOKENS.ganttProduction,
  TOKENS.stateComplete,
  TOKENS.ganttClean,
  '#7a5fb0',
  TOKENS.ganttChangeover,
]

// ─── Shared layout primitives ─────────────────────────────────────────────────

const OWeekTrack = memo(
  ({
    height = 22,
    bg = TOKENS.ganttIdle,
    children,
  }: {
    height?: number
    bg?: string
    children?: React.ReactNode
  }) => (
    <Box
      sx={{
        position: 'relative',
        height,
        bgcolor: bg,
        border: `1px solid ${TOKENS.line200}`,
        borderRadius: '3px',
        overflow: 'hidden',
      }}
    >
      {[1, 2, 3, 4, 5, 6].map((d) => (
        <Box
          key={d}
          sx={{
            position: 'absolute',
            left: pct(d * 24),
            top: 0,
            bottom: 0,
            width: '1px',
            bgcolor: 'rgba(14,27,34,0.12)',
            zIndex: 1,
          }}
        />
      ))}
      {children}
    </Box>
  )
)
OWeekTrack.displayName = 'OWeekTrack'

const OBand = memo(
  ({
    start,
    end,
    color,
    title: ttl,
    outline,
    radius = 2,
    top = 2,
    bottom = 2,
  }: {
    start: number
    end: number
    color: string
    title?: string
    outline?: string
    radius?: number
    top?: number
    bottom?: number
  }) => {
    const width = Math.max(0, Math.min(TOTAL_HOURS, end) - Math.max(0, start))
    if (width <= 0) return null
    return (
      <Tooltip title={ttl ?? ''} arrow disableHoverListener={!ttl}>
        <Box
          sx={{
            position: 'absolute',
            left: pct(Math.max(0, start)),
            width: pct(width),
            top,
            bottom,
            zIndex: 2,
            bgcolor: color,
            border: outline ? `1.5px solid ${outline}` : 'none',
            borderRadius: radius,
          }}
        />
      </Tooltip>
    )
  }
)
OBand.displayName = 'OBand'

const WeekDayHeader = memo(() => (
  <Box sx={{ display: 'flex', mb: '4px' }}>
    {DAY_LABELS.map((d) => (
      <Box
        key={d}
        sx={{
          flex: 1,
          textAlign: 'center',
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: '0.05em',
          color: TOKENS.ink400,
        }}
      >
        {d}
      </Box>
    ))}
  </Box>
))
WeekDayHeader.displayName = 'WeekDayHeader'

// ─── PanelHead ────────────────────────────────────────────────────────────────

type PanelMetric = {
  label: string
  value: number | string
  unit?: string
  tone?: string
}

const PanelHead = memo(
  ({
    icon,
    title,
    sub,
    metrics,
  }: {
    icon: React.ReactNode
    title: string
    sub?: string
    metrics?: PanelMetric[]
  }) => (
    <Box
      sx={{
        flexShrink: 0,
        borderBottom: `1px solid ${TOKENS.line200}`,
        px: '18px',
        py: '11px',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
      }}
    >
      <Box
        sx={{
          color: TOKENS.stateComplete,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.18 }}>
        <Box sx={{ ...TYPE.h2 }}>{title}</Box>
        {sub && <Box sx={{ fontSize: 10.5, color: TOKENS.ink400 }}>{sub}</Box>}
      </Box>
      <Box sx={{ flex: 1 }} />
      {metrics && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {metrics.map((m, i) => (
            <Box
              key={m.label}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1px',
                pr: i < metrics.length - 1 ? '18px' : 0,
                borderRight:
                  i < metrics.length - 1
                    ? `1px solid ${TOKENS.line200}`
                    : 'none',
                mr: i < metrics.length - 1 ? '18px' : 0,
              }}
            >
              <Box
                sx={{
                  fontSize: 9,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                  color: TOKENS.ink400,
                }}
              >
                {m.label}
              </Box>
              <Box
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: 17,
                  fontWeight: 600,
                  lineHeight: 1,
                  color: m.tone ?? TOKENS.ink900,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {m.value}
                {m.unit && (
                  <Box
                    component="span"
                    sx={{ fontSize: 10, color: TOKENS.ink400, ml: '2px' }}
                  >
                    {m.unit}
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
)
PanelHead.displayName = 'PanelHead'

// ─── Data types & derivation ──────────────────────────────────────────────────

type SyncMember = {
  name: string
  runs: { start: number; end: number }[]
  coverage: number
}
type SyncGroupData = {
  id: number
  name: string
  color: string
  temp: string
  members: SyncMember[]
  adherence: number
  drift: string[]
  windows: [number, number][]
}

function deriveSyncGroups(
  syncGroups: SynchronizedLinesRowType[],
  outputRows: InputRow[]
): SyncGroupData[] {
  return syncGroups
    .filter((g) => {
      const type = String(g.productMasterType ?? '').toLowerCase()
      return type === 'temperature' || type === ''
    })
    .map((g, gi) => {
      const lines = String(g.lines ?? '')
        .split(',')
        .map((l) => l.trim())
        .filter(Boolean)
      const color = SYNC_COLORS[gi % SYNC_COLORS.length]
      const temp = (g.groupName.match(/(\d+)/) ?? [])[1] ?? ''

      // Collect actual production runs per member line
      const lineRunsMap = new Map<string, { start: number; end: number }[]>()
      for (const lineName of lines) {
        const runs: { start: number; end: number }[] = []
        for (const row of outputRows) {
          if (
            row.line !== lineName ||
            row.line_start_time == null ||
            row.line_end_time == null
          )
            continue
          runs.push({ start: row.line_start_time, end: row.line_end_time })
        }
        lineRunsMap.set(lineName, runs)
      }

      // Union of all member production time = required window proxy
      const unionHours = new Set<number>()
      for (const [, runs] of lineRunsMap)
        for (const r of runs)
          for (
            let h = Math.floor(r.start);
            h < Math.ceil(r.end) && h < TOTAL_HOURS;
            h++
          )
            unionHours.add(h)

      // Per-line coverage vs the group union
      const members: SyncMember[] = lines.map((lineName) => {
        const runs = lineRunsMap.get(lineName) ?? []
        const lineHours = new Set<number>()
        for (const r of runs)
          for (
            let h = Math.floor(r.start);
            h < Math.ceil(r.end) && h < TOTAL_HOURS;
            h++
          )
            lineHours.add(h)
        return {
          name: lineName,
          runs,
          coverage: unionHours.size > 0 ? lineHours.size / unionHours.size : 0,
        }
      })

      // Convert union hours to contiguous window segments
      const sortedH = Array.from(unionHours).sort((a, b) => a - b)
      const windows: [number, number][] = []
      let ws = -1,
        we = -1
      for (const h of sortedH) {
        if (ws < 0) {
          ws = h
          we = h + 1
        } else if (h === we) {
          we++
        } else {
          windows.push([ws, we])
          ws = h
          we = h + 1
        }
      }
      if (ws >= 0) windows.push([ws, we])

      const adherence =
        members.length > 0
          ? members.reduce((s, m) => s + m.coverage, 0) / members.length
          : 0
      const drift = members.filter((m) => m.coverage < 0.62).map((m) => m.name)

      return {
        id: g.id,
        name: g.groupName,
        color,
        temp,
        members,
        adherence,
        drift,
        windows,
      }
    })
}

// ─── SyncCard ─────────────────────────────────────────────────────────────────

const SyncCard = memo(({ group }: { group: SyncGroupData }) => {
  const adh = Math.round(group.adherence * 100)
  const adhTone =
    adh >= 85
      ? TOKENS.stateComplete
      : adh >= 70
        ? TOKENS.stateRunning
        : TOKENS.ganttDowntime

  return (
    <Box
      sx={{
        border: `1px solid ${TOKENS.line200}`,
        borderRadius: '3px',
        bgcolor: '#fff',
        mb: '14px',
        overflow: 'hidden',
      }}
    >
      {/* Card header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          px: '14px',
          py: '11px',
          borderBottom: `1px solid ${TOKENS.line100}`,
          bgcolor: TOKENS.surface50,
        }}
      >
        <Box
          sx={{
            width: 11,
            height: 11,
            borderRadius: '3px',
            bgcolor: group.color,
            flexShrink: 0,
          }}
        />
        <Box
          sx={{
            fontSize: 13.5,
            fontWeight: 700,
            color: TOKENS.ink900,
            flex: 1,
            fontFamily: FONT_SANS,
          }}
        >
          {group.name}
        </Box>
        {group.temp && (
          <SchChip
            tone="neutral"
            icon={<ThermostatIcon sx={{ fontSize: 13 }} />}
          >
            {group.temp} °C
          </SchChip>
        )}
        <Box sx={{ fontSize: 10.5, color: TOKENS.ink400 }}>Adherence</Box>
        <Box
          sx={{
            fontFamily: FONT_MONO,
            fontSize: 16,
            fontWeight: 700,
            color: adhTone,
          }}
        >
          {adh}%
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: '12px 14px 14px' }}>
        {/* Day header */}
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ width: 70, minWidth: 70 }} />
          <Box sx={{ flex: 1 }}>
            <WeekDayHeader />
          </Box>
          <Box sx={{ width: 52 }} />
        </Box>

        {/* Per-line rows */}
        {group.members.map((m) => {
          const covPct = Math.round(m.coverage * 100)
          const covTone =
            m.coverage >= 0.85
              ? TOKENS.stateComplete
              : m.coverage >= 0.62
                ? TOKENS.stateRunning
                : TOKENS.ganttDowntime
          return (
            <Box
              key={m.name}
              sx={{ display: 'flex', alignItems: 'center', height: 24 }}
            >
              <Box
                sx={{
                  width: 70,
                  minWidth: 70,
                  textAlign: 'right',
                  pr: '10px',
                  fontSize: 11,
                  fontFamily: FONT_MONO,
                  fontWeight: 600,
                  color: TOKENS.ink700,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {m.name}
              </Box>
              <Box sx={{ flex: 1 }}>
                <OWeekTrack height={16}>
                  {/* Required windows — transparent with colour outline */}
                  {group.windows.map((w, i) => (
                    <OBand
                      key={`w${i}`}
                      start={w[0]}
                      end={w[1]}
                      color="transparent"
                      outline={group.color}
                      radius={2}
                      top={0}
                      bottom={0}
                    />
                  ))}
                  {/* Actual production runs — solid fill */}
                  {m.runs.map((r, i) => (
                    <OBand
                      key={`r${i}`}
                      start={r.start}
                      end={r.end}
                      color={group.color}
                      radius={2}
                    />
                  ))}
                </OWeekTrack>
              </Box>
              <Box
                sx={{
                  width: 52,
                  textAlign: 'right',
                  fontFamily: FONT_MONO,
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: covTone,
                }}
              >
                {covPct}%
              </Box>
            </Box>
          )
        })}

        {/* Footer — status + legend */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            mt: '10px',
            pl: '70px',
          }}
        >
          {group.drift.length === 0 ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: 11.5,
                color: TOKENS.stateComplete,
                fontWeight: 600,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 15 }} />
              All {group.members.length} lines hold the{' '}
              {group.temp ? `${group.temp} °C` : group.name} block
            </Box>
          ) : (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: 11.5,
                color: TOKENS.ganttDowntime,
                fontWeight: 600,
              }}
            >
              <LinkOffIcon sx={{ fontSize: 15 }} />
              {group.drift.join(', ')} drift
              {group.drift.length === 1 ? 's' : ''} out of sync
            </Box>
          )}
          <Box sx={{ flex: 1 }} />
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              fontSize: 10,
              color: TOKENS.ink400,
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 9,
                borderRadius: '2px',
                border: `1.5px solid ${group.color}`,
              }}
            />
            required window
          </Box>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              fontSize: 10,
              color: TOKENS.ink400,
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 9,
                borderRadius: '2px',
                bgcolor: group.color,
              }}
            />
            actual run
          </Box>
        </Box>
      </Box>
    </Box>
  )
})
SyncCard.displayName = 'SyncCard'

// ─── Main export ──────────────────────────────────────────────────────────────

export const ScheduleTemperatureView = memo(
  ({
    outputRows,
    synchronizedLines,
  }: {
    outputRows: InputRow[]
    synchronizedLines: SynchronizedLinesRowType[]
    // productionRequirements kept for call-site compat but not needed by this design
    productionRequirements?: ProductionRequirementRowType[]
  }) => {
    const groups = useMemo(
      () => deriveSyncGroups(synchronizedLines, outputRows),
      [synchronizedLines, outputRows]
    )
    const drifting = groups.filter((g) => g.drift.length > 0).length
    const avgAdh =
      groups.length > 0
        ? Math.round(
            (groups.reduce((s, g) => s + g.adherence, 0) / groups.length) * 100
          )
        : 0

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          bgcolor: TOKENS.surface0,
        }}
      >
        <PanelHead
          icon={<SyncIcon sx={{ fontSize: 19 }} />}
          title="Sync groups"
          sub="Lines that must run a shared temperature block together"
          metrics={[
            { label: 'Groups', value: groups.length },
            {
              label: 'Avg adherence',
              value: avgAdh,
              unit: '%',
              tone: avgAdh >= 85 ? TOKENS.stateComplete : TOKENS.stateRunning,
            },
            {
              label: 'Drifting',
              value: drifting,
              tone: drifting ? TOKENS.ganttDowntime : TOKENS.stateComplete,
            },
          ]}
        />
        <Box sx={{ overflow: 'auto', px: '18px', py: '16px', pb: '24px' }}>
          {groups.length === 0 ? (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                color: TOKENS.ink400,
                fontSize: 12.5,
              }}
            >
              No temperature sync groups configured.
            </Box>
          ) : (
            groups.map((g) => <SyncCard key={g.id} group={g} />)
          )}
        </Box>
      </Box>
    )
  }
)
ScheduleTemperatureView.displayName = 'ScheduleTemperatureView'
